'use client'

import { Avatar } from '@acko/avatar'
import { Button } from '@acko/button'
import { Separator } from '@acko/separator'
import { Typography } from '@acko/typography'
import Scroll from '@components/Scroll/scroll'
import AnalyticsViewPage from '@components/utils/analyticsViewPage'

import React, { useEffect, useMemo, useState } from 'react'
import { Lob } from 'src/enums/Lob'

import { Footer } from '@components/Home/Footer'
import { FooterDataType } from '@quark/footer'
import { BikeRegistrationInputModal, type BikeRegistrationInputModalPresentation } from './BikeRegistrationInputModal'
import { CoveragesSectionClient } from './CoveragesSectionClient'
import { StickyFooterClient } from './StickyFooterClient'
import { HeroContentClient } from './HeroContentClient'
import { SimpleBikeFlowModal } from './SimpleBikeFlowModal'
import { getBikeContinueInsuringJourney, getHeroContentViewModel } from './heroWidgetBehavior.mjs'
import styles from './page.module.css'
import { useBikeRegistrationFlow } from './useBikeRegistrationFlow'

/* Asset URLs */

/** Trust bar — overlapping avatars next to “Trusted by 5 Cr+ Indians” (CMS) */
const imgTrustProfile1 = 'https://acko-cms-dev.s3.ap-south-1.amazonaws.com/Avatar_2_6f2e7c6937.webp'
const imgTrustProfile2 = 'https://acko-cms-dev.s3.ap-south-1.amazonaws.com/Avatar_1_6e3979f8dc.webp'
const imgTrustProfile3 = 'https://acko-cms-dev.s3.ap-south-1.amazonaws.com/Avatar_3_083e7f7222.webp'
/** Trust bar — right column award icons (CMS), order: direct insurer → app → IRDAI */
const imgTrustBarDirectInsurer = 'https://acko-cms-dev.s3.ap-south-1.amazonaws.com/direct_insurer_icon_6eeae3fda0.svg'
const imgTrustBarInsuranceApp = 'https://acko-cms-dev.s3.ap-south-1.amazonaws.com/insurance_app_icon_afd2cc7b30.svg'
const imgTrustBarIrdai = 'https://acko-cms-dev.s3.ap-south-1.amazonaws.com/irdai_icon_9a26634949.svg'
const imgAppleIcon = 'https://acko-cms-dev.s3.ap-south-1.amazonaws.com/ios_icon_7845cf595b.svg'
const imgGooglePlay = 'https://acko-cms-dev.s3.ap-south-1.amazonaws.com/google_play_icon_be73fb1823.svg'
const imgGoogleIcon = 'https://acko-cms-dev.s3.ap-south-1.amazonaws.com/google_review_icon_bd83deb745.svg'
const imgReviewX = 'https://acko-cms-dev.s3.ap-south-1.amazonaws.com/meta_x_icon_a95d71646e.svg'
const imgReviewLinkedin = 'https://acko-cms-dev.s3.ap-south-1.amazonaws.com/Linkedin_icon_2511d52fd2.svg'
const imgZeroCommission = 'https://acko-cms-dev.s3.ap-south-1.amazonaws.com/zero_commission_illustration_f804523953.svg'
const imgHighIdv = 'https://acko-cms-dev.s3.ap-south-1.amazonaws.com/high_idv_illustration_6134b8fea1.svg'
const imgGarage = 'https://acko-cms-dev.s3.ap-south-1.amazonaws.com/garage_illustration_e65f174c3f.svg'
/** Electric bike image */
/** Comparison table assets from Figma */
const imgAckoLogo = 'https://acko-brand.ackoassets.com/brand/vector-svg/gradient/gradient-horizontal.svg'
/** Hero band: `brandSubtle` = `--color-primary-subtle` (purple marketing tint); `default` = `--color-surface`. */
type HeroSectionSurface = 'brandSubtle' | 'default'

const HeroSection = ({
  children,
  surface = 'brandSubtle',
  className = '',
}: {
  children: React.ReactNode
  surface?: HeroSectionSurface
  className?: string
}) => (
  <section
    className={`w-full ${
      surface === 'brandSubtle' ? styles.heroSectionBrand : styles.heroSectionDefault
    } ${className}`}>
    {children}
  </section>
)

const Section = ({
  id,
  children,
  className = '',
  innerClassName = '',
}: {
  children: React.ReactNode
  className?: string
  /** Anchor id for in-page nav / scroll targets */
  id?: string
  /** Classes on the inner `max-w-[1280px] px-0` container (vertical rhythm comes from `.docMainColumn` gap). */
  innerClassName?: string
}) => (
  <section id={id} className={['w-full', className, id ? styles.sectionScrollTarget : ''].filter(Boolean).join(' ')}>
    <div className={`mx-auto w-full max-w-[1280px] px-16 md:px-32 lg:px-[80px] xl:px-0 ${innerClassName}`.trim()}>
      {children}
    </div>
  </section>
)

/* ─── Trust Bar ─────────────────────────────────────────────────────────────── */
/*
 * Figma: Trust-section w=1440 h=146
 *   Container: x=80 y=48 w=1280 h=50 → py-48 outer, px-80 aligns to 80px edge
 *   Profile overlap: each 48×48, offset x=25 (second) x=50 (third) → -ml-24
 *   Left text: x=114 → 98px profiles + 16px gap = gap-16
 *   Award container: x=603 → right side 677px wide, awards gap-24 between each
 *   Award icon: 48×48, content x=68 → 48+20=68 → gap-20
 *   Dividers at x=196, x=433 → spans 196, then 220, then 220 → gap-24 between awards
 */
const TrustBar = () => (
  <section className={`w-full ${styles.trustBarSection}`} id="mobile-sticky-footer-trigger">
    <div
      className={`${styles.trustBar} mx-auto flex flex-col gap-20 lg:flex-row lg:items-center lg:justify-between px-16 md:px-32 lg:px-[80px] xl:px-0 py-24 md:py-32 lg:py-48 min-w-0`}>
      <div className="flex gap-16 items-center justify-between lg:justify-start shrink-0 min-w-0">
        <Typography
          variant="display-sm"
          as="h2"
          color="primary"
          className="text-[24px] leading-[30px] lg:text-[var(--font-heading-xl-size)] lg:leading-[var(--font-heading-xl-line)] whitespace-nowrap">
          Trusted by 5 Cr+ Indians
        </Typography>
        <div className="flex items-center">
          {[imgTrustProfile1, imgTrustProfile2, imgTrustProfile3].map((src, i) => (
            <div
              key={`avatar-${i}`}
              className={`border-2 border-white shrink-0 w-48 h-48 rounded-full overflow-hidden ${
                i > 0 ? styles.trustAvatarOverlap : ''
              }`}>
              <img src={src} alt={`Customer avatar ${i + 1}`} className="w-full h-full object-cover" loading="lazy" />
            </div>
          ))}
        </div>
      </div>
      <div className="flex gap-24 items-center justify-around lg:justify-end shrink-0 min-w-0">
        {[
          { line1: 'Best direct', line2: 'insurer 2025', icon: imgTrustBarDirectInsurer },
          { line1: "India's #1*", line2: 'insurance app', icon: imgTrustBarInsuranceApp },
          { line1: 'IRDAI', line2: 'reg no. 157', icon: imgTrustBarIrdai },
        ].map((award, i) => (
          <React.Fragment key={award.line1}>
            {i > 0 && <Separator orientation="vertical" className="h-[50px] hidden md:block" decorative />}
            <div className="flex flex-col lg:flex-row gap-8 lg:gap-20 items-center">
              <div className="relative shrink-0 w-48 h-48">
                <img alt="" className="w-full h-full object-contain" src={award.icon} />
              </div>
              <Typography
                variant="body-md"
                weight="medium"
                color="primary"
                as="p"
                className="m-0 text-[var(--font-body-sm-size)] leading-[var(--font-body-sm-line)] lg:text-[var(--font-body-md-size)] lg:leading-[var(--font-body-md-line)] text-center lg:text-left">
                {award.line1}
                <br />
                {award.line2}
              </Typography>
            </div>
          </React.Fragment>
        ))}
      </div>
    </div>
  </section>
)

/* ─── Coverages Section — see CoveragesSectionClient.tsx ────────────────────── */

const WhyAckoSection = () => (
  <div
    id="why-acko"
    className={`${styles.whyAckoPanel} p-20 md:p-24 lg:p-24 ${styles.docSectionBlockGap} ${styles.sectionScrollTarget}`}>
    <div className="flex flex-col gap-12">
      <Typography variant="display-sm" as="h2" weight="semibold" color="primary">
        Buy directly from us. <span className="lg:inline block">Get more. Save more.</span>
      </Typography>
      <Typography variant="body-lg" color="secondary">
        We don&apos;t sell policies on other websites
      </Typography>
    </div>
    <div className="flex flex-col md:flex-row gap-16 lg:gap-24 items-stretch w-full min-w-0">
      {[
        {
          icon: (
            <img alt="Zero commission illustration" className="w-full h-full object-contain" src={imgZeroCommission} />
          ),
          title: 'Commission-free prices',
          desc: "We don't sell policies on other websites so your full premium comes back to you",
        },
        {
          icon: <img alt="Higher IDV illustration" className="w-full h-full object-contain" src={imgHighIdv} />,
          title: 'Higher insured value',
          desc: 'We offer higher insured declared value options for the same price as others',
        },
        {
          icon: <img alt="Garage network illustration" className="w-full h-full object-contain" src={imgGarage} />,
          title: 'Claims in hours, not days',
          desc: 'Minor claims approved in as little as 2 hours, fully online',
        },
      ].map(item => (
        <div
          key={item.title}
          className={`${styles.dsCard} p-20 md:p-24 lg:p-24 ${styles.minW0} flex-1 flex flex-col gap-12`}
          data-bg="white"
          data-variant="outlined">
          <div className="w-64 h-64 shrink-0">{item.icon}</div>
          <Typography variant="heading-md" as="h3" color="primary">
            {item.title}
          </Typography>
          <Typography variant="body-md" color="secondary">
            {item.desc}
          </Typography>
        </div>
      ))}
    </div>
  </div>
)

/* ─── Review platform icons mapped from socialMedia field ───────────────── */
const PLATFORM_ICONS: Record<string, string> = {
  twitter: imgReviewX,
  x: imgReviewX,
  linkedin: imgReviewLinkedin,
  playstore: imgGooglePlay,
  play_store: imgGooglePlay,
  appstore: imgAppleIcon,
  app_store: imgAppleIcon,
  google: imgGoogleIcon,
}

type CmsReview = {
  id: number
  attributes: {
    userName: string
    userFeedback: string
    socialMedia?: string
    userSocialInfo?: string
    lob?: string
    createdAt?: string
  }
}

/* Fallback reviews shown when CMS returns no bike-specific data */
const FALLBACK_REVIEWS: CmsReview[] = [
  {
    id: 1,
    attributes: {
      userName: 'Rahul Ram',
      userFeedback:
        "The entire repair expenses were covered - I didn't have to pay a single rupee out of pocket. A huge thank you to ACKO Support for their timely support and trustworthy service.",
      socialMedia: 'linkedin',
      userSocialInfo: '',
    },
  },
  {
    id: 2,
    attributes: {
      userName: 'Vadivelan Udayakumar',
      userFeedback:
        'Had a good experience with insurance claim for my bike. The executive Chethan was able to understand the issues and helped with the claim. The process of inspection and claim processing was great and quick.',
      socialMedia: '',
      userSocialInfo: '',
    },
  },
  {
    id: 3,
    attributes: {
      userName: 'Agarwalakash45',
      userFeedback:
        'Filed a bike insurance claim and it got settled in just 1 day! The process was smooth, simple, and stress-free. Really impressed with ACKO - highly recommend!',
      socialMedia: 'app_store',
      userSocialInfo: '',
    },
  },
  {
    id: 4,
    attributes: {
      userName: 'Vikas Saini',
      userFeedback:
        'I initiated a claim for my Splendor bike and it was settled in just 2 days. Everything was online and fast. Best customer service experience ever.',
      socialMedia: '',
      userSocialInfo: '',
    },
  },
  {
    id: 5,
    attributes: {
      userName: 'Ajay shah',
      userFeedback:
        'Great bike insurance plan. Renewal was super easy, claim process was fast. ACKO team was very responsive. Would definitely recommend to all bike owners.',
      socialMedia: 'app_store',
      userSocialInfo: '',
    },
  },
  {
    id: 6,
    attributes: {
      userName: 'Priya Sharma',
      userFeedback:
        'ACKO is really best and simple to use. I have taken my bike insurance from ACKO it was really smooth. The policy itself is very easy to understand and presented in such a simple way.',
      socialMedia: 'google',
      userSocialInfo: '',
    },
  },
]

const ReviewCard = ({ review }: { review: CmsReview }) => {
  const { userName, userFeedback, socialMedia, userSocialInfo } = review.attributes
  const platformIcon = socialMedia ? PLATFORM_ICONS[socialMedia.toLowerCase()] : imgGoogleIcon

  // Generate initials from user name
  const getInitials = (name: string) =>
    name
      .split(' ')
      .slice(0, 2)
      .map(word => word.charAt(0).toUpperCase())
      .join('')

  return (
    <div
      className={`${styles.dsCard} ${styles.reviewCard} p-20 md:p-24 lg:p-24 flex flex-col gap-16 min-h-[240px]`}
      data-bg="white"
      data-variant="elevated">
      <div className="flex items-start justify-between">
        <div className="flex gap-12 items-center">
          <Avatar
            alt={userName}
            initials={getInitials(userName)}
            size="lg"
            shape="circle"
            className="w-48 h-48 shrink-0"
          />
          <div className="flex flex-col gap-2">
            <Typography variant="body-md" weight="medium" color="primary">
              {userName}
            </Typography>
            {userSocialInfo && (
              <Typography variant="caption" color="secondary">
                {userSocialInfo}
              </Typography>
            )}
          </div>
        </div>
        {platformIcon && <img alt="" className="w-24 h-24 object-contain shrink-0" src={platformIcon} />}
      </div>
      <Typography variant="body-md" color="primary" className="line-clamp-6">
        {userFeedback}
      </Typography>
    </div>
  )
}

const ReviewsSection = ({ reviews }: { reviews: CmsReview[] }) => (
  <div id="trust-reviews" className={`${styles.sectionScrollTarget}`}>
    <div className="flex flex-col gap-40">
      <Typography variant="display-sm" as="h2" weight="semibold" color="primary">
        See why 5 crore+ Indians <span className="lg:inline block">trust ACKO</span>
      </Typography>

      {/* Rating Cards */}
      <div className="flex flex-row gap-16 lg:gap-24 items-stretch w-full">
        {[
          { icon: imgAppleIcon, rating: '4.8', label: 'App store' },
          { icon: imgGooglePlay, rating: '4.6', label: 'Play store' },
          { icon: imgGoogleIcon, rating: '4.6', label: 'Google' },
        ].map(store => (
          <div
            key={store.label}
            className={`${styles.dsCard} p-16 lg:p-24 flex flex-1 flex-col lg:flex-row gap-8 lg:gap-16 items-center lg:items-center`}
            data-bg="info-subtle"
            data-variant="flat">
            <div className="w-48 h-48 bg-white rounded-full flex items-center justify-center shrink-0">
              <img alt="" className="w-28 h-28 object-contain" src={store.icon} />
            </div>
            <div className="flex flex-col items-center lg:items-start">
              <div className="flex items-baseline">
                <Typography
                  variant="heading-xl"
                  as="p"
                  color="primary"
                  className="text-[22px] leading-[26px] lg:text-[var(--font-heading-xl-size)] lg:leading-[var(--font-heading-xl-line)]">
                  {store.rating}
                </Typography>
                <Typography
                  variant="body-md"
                  as="span"
                  color="primary"
                  className="ml-2 text-[var(--font-body-sm-size)] leading-[var(--font-body-sm-line)] lg:text-[var(--font-body-md-size)]">
                  /5
                </Typography>
              </div>
              <Typography
                variant="body-md"
                color="secondary"
                className="text-[var(--font-body-sm-size)] leading-[var(--font-body-sm-line)] lg:text-[var(--font-body-md-size)] lg:leading-[var(--font-body-md-line)]">
                {store.label}
              </Typography>
            </div>
          </div>
        ))}
      </div>
    </div>

    {/* Review Cards — horizontal scroll on mobile, grid on desktop */}
    <div className={`${styles.reviewCardsScroll} flex lg:grid lg:grid-cols-3 gap-16 lg:gap-24 w-full mt-16 lg:mt-24`}>
      {reviews.map(review => (
        <ReviewCard key={review.id} review={review} />
      ))}
    </div>
  </div>
)

const CTASection = ({ onCheckPriceClick }: { onCheckPriceClick: () => void }) => (
  <section className={`${styles.ctaSection} w-full py-40 lg:py-64 px-16 md:px-32 lg:px-[80px] xl:px-0`}>
    <div className={`${styles.ctaInner} flex flex-col gap-4 lg:gap-8 items-center text-center mx-auto`}>
      <Typography variant="display-sm" as="h2" weight="semibold" color="invert" className="text-balance">
        Insure your bike in just 2 minutes
      </Typography>
      <Typography variant="body-lg" color="invert" className={styles.ctaSubtext}>
        No paperwork. No agents. Get instant coverage at the best price.
      </Typography>
      <div className="flex justify-center w-full md:w-auto mt-16">
        <Button
          type="button"
          variant="secondary"
          size="lg"
          className={`${styles.btnOnPrimary} w-full md:w-auto`}
          onClick={onCheckPriceClick}>
          Check ACKO price
        </Button>
      </div>
    </div>
  </section>
)

/* ─── Page-level JSON-LD schemas ─────────────────────────────────────────── */

const PAGE_SCHEMAS = [
  {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: 'Home', item: 'https://www.acko.com/' },
      { '@type': 'ListItem', position: 2, name: 'Bike Insurance', item: 'https://www.acko.com/bike-insurance/' },
    ],
  },
  {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: [
      {
        '@type': 'Question',
        name: 'What is bike insurance?',
        acceptedAnswer: {
          '@type': 'Answer',
          text: 'Bike insurance is a contract between you and an insurance company where the insurer agrees to provide financial protection against losses arising from accidents, theft, or damage to your bike, in exchange for a premium. It is mandatory in India under the Motor Vehicles Act, 1988 — at minimum, third-party liability coverage is required.',
        },
      },
      {
        '@type': 'Question',
        name: 'Is bike insurance mandatory in India?',
        acceptedAnswer: {
          '@type': 'Answer',
          text: 'Yes, third-party bike insurance is mandatory under the Motor Vehicles Act, 1988. Riding without at least a third-party policy is a punishable offence with fines up to ₹2,000 and/or imprisonment for up to 3 months. Comprehensive coverage is optional but strongly recommended.',
        },
      },
      {
        '@type': 'Question',
        name: 'What is covered in comprehensive bike insurance?',
        acceptedAnswer: {
          '@type': 'Answer',
          text: 'Comprehensive bike insurance covers accidental damage to your bike, theft, fire, natural calamities (floods, earthquakes, cyclones), third-party liability (unlimited for bodily injury/death), and transit damage. You can also add optional covers like zero depreciation, engine protection, personal accident cover, and roadside assistance.',
        },
      },
      {
        '@type': 'Question',
        name: 'How do I file a bike insurance claim at ACKO?',
        acceptedAnswer: {
          '@type': 'Answer',
          text: 'You can file a claim through the ACKO app or website in 3 steps: (1) Go to your policy and tap the Claim button, (2) Upload photos of the damage and describe the incident, (3) Drop your bike at any authorised service centre for cashless repair. Most claims are settled within 1-7 days.',
        },
      },
      {
        '@type': 'Question',
        name: 'What is IDV in bike insurance?',
        acceptedAnswer: {
          '@type': 'Answer',
          text: "IDV (Insured Declared Value) is the current market value of your bike after factoring in depreciation. It represents the maximum amount your insurer will pay in case of total loss or theft. A higher IDV means better coverage but a slightly higher premium — choose based on your bike's age and condition.",
        },
      },
      {
        '@type': 'Question',
        name: 'What is zero depreciation cover?',
        acceptedAnswer: {
          '@type': 'Answer',
          text: 'Zero depreciation (zero dep) cover ensures you receive the full cost of replaced parts during a claim without any deduction for depreciation. Without it, you may pay 30-50% of parts cost out of pocket. It is highly recommended for bikes under 5 years old.',
        },
      },
    ],
  },
  {
    '@context': 'https://schema.org',
    '@type': 'Product',
    name: 'Bike Insurance',
    description:
      'Buy or renew two-wheeler insurance online from ACKO. Comprehensive, Own Damage & Third Party plans starting at ₹457/year. Zero commission, instant policy, and quick claims support.',
    brand: { '@type': 'Organization', name: 'ACKO General Insurance' },
    category: 'Two Wheeler Insurance',
    offers: [
      {
        '@type': 'AggregateOffer',
        name: 'Third Party Bike Insurance',
        priceCurrency: 'INR',
        lowPrice: '714',
        highPrice: '2804',
        description: 'IRDAI-fixed third party bike insurance rates for all two-wheelers in India',
      },
      {
        '@type': 'AggregateOffer',
        name: 'Comprehensive Bike Insurance',
        priceCurrency: 'INR',
        lowPrice: '112',
        highPrice: '5000',
        description: 'Comprehensive bike insurance covering own damage, theft, fire, and third-party liability',
      },
    ],
  },
  {
    '@context': 'https://schema.org',
    '@type': 'HowTo',
    name: 'How to file a bike insurance claim at ACKO',
    step: [
      {
        '@type': 'HowToStep',
        position: 1,
        name: 'Open the ACKO app and tap Claim',
        text: 'In the ACKO app, go to your bike policy and tap the Claim button. Take clear photos of the damage and describe the incident.',
      },
      {
        '@type': 'HowToStep',
        position: 2,
        name: 'Upload documents',
        text: 'Submit your RC, DL, and damage photos through the app. For theft claims, include an FIR. The app guides you step by step.',
      },
      {
        '@type': 'HowToStep',
        position: 3,
        name: 'Get claim support',
        text: 'Drop your bike at any authorised network garage. ACKO will guide you through the claim process. Most claims are resolved within 1–7 days.',
      },
    ],
  },
]

/* ─── Page ──────────────────────────────────────────────────────────────────── */

interface BikeInsurancePageClientProps {
  heroContentData?: any
  initialRegistrationNumber?: string
  journeyData?: any[]
  regexPatternsData?: string[]
  reviews?: CmsReview[] | null
  footerData: null | FooterDataType
}

const BikeInsurancePageClient = ({
  heroContentData = null,
  initialRegistrationNumber = '',
  journeyData = [],
  regexPatternsData = [],
  footerData = null,
}: BikeInsurancePageClientProps) => {
  const reviewsToShow = FALLBACK_REVIEWS
  const [showInputModal, setShowInputModal] = useState(false)
  const [inputModalPresentation, setInputModalPresentation] = useState<BikeRegistrationInputModalPresentation>('modal')
  const [lastRegistrationSource, setLastRegistrationSource] = useState<'hero' | 'modal'>('hero')
  const heroViewModel = useMemo(() => getHeroContentViewModel(heroContentData), [heroContentData])
  const continueInsuringJourney = useMemo(() => getBikeContinueInsuringJourney(journeyData), [journeyData])
  const segmentEventCommonProperties = useMemo(
    () => ({
      productWidgetData: {
        Content: {
          data: {
            attributes: {
              widget: 'bike-seo-widget',
              productWidgetMainHeading: heroViewModel.heading,
            },
          },
        },
      },
    }),
    [heroViewModel.heading]
  )
  const heroRegistrationFlow = useBikeRegistrationFlow({
    ctaText: heroViewModel.widget.ctaText,
    initialRegistrationNumber,
    regexPatternsData,
    segmentHeading: heroViewModel.heading,
  })
  const inputModalRegistrationFlow = useBikeRegistrationFlow({
    ctaText: heroViewModel.widget.ctaText,
    initialRegistrationNumber,
    regexPatternsData,
    segmentHeading: heroViewModel.heading,
  })
  const activeRegistrationFlow = lastRegistrationSource === 'modal' ? inputModalRegistrationFlow : heroRegistrationFlow
  const isIllogicalFlowModalOpen =
    heroRegistrationFlow.showIllogicalFlowModal || inputModalRegistrationFlow.showIllogicalFlowModal

  useEffect(() => {
    if (isIllogicalFlowModalOpen) {
      setShowInputModal(false)
    }
  }, [isIllogicalFlowModalOpen])

  useEffect(() => {
    document.documentElement.style.overflowY = showInputModal || isIllogicalFlowModalOpen ? 'hidden' : 'scroll'

    return () => {
      document.documentElement.style.overflowY = 'scroll'
    }
  }, [isIllogicalFlowModalOpen, showInputModal])

  const submitFromHero = async () => {
    setLastRegistrationSource('hero')
    await heroRegistrationFlow.fetchAndInvokeHandleSubmit()
  }

  const submitFromInputModal = async () => {
    setLastRegistrationSource('modal')
    await inputModalRegistrationFlow.fetchAndInvokeHandleSubmit()
  }

  const getRegistrationFormProps = (registrationFlow: typeof heroRegistrationFlow, onSubmit: () => Promise<void>) => ({
    ctaText: heroViewModel.widget.ctaText,
    disclaimer: heroViewModel.widget.disclaimer,
    heading: heroViewModel.widget.heading,
    inputPlaceholder: heroViewModel.widget.inputPlaceholder,
    isFetching: registrationFlow.isFetching,
    onBlur: registrationFlow.handleRegistrationNumberBlur,
    onChange: registrationFlow.handleRegistrationNumberChange,
    onSubmit,
    registrationError: registrationFlow.registrationError,
    regNumber: registrationFlow.regNumber,
    showError: registrationFlow.showError,
  })

  const openInputModal = (presentation: BikeRegistrationInputModalPresentation) => {
    setInputModalPresentation(presentation)
    setShowInputModal(true)
  }

  const handleEditRegistrationFromFlowModal = () => {
    activeRegistrationFlow.resetIllogicalFlow()
    if (lastRegistrationSource === 'modal') {
      setShowInputModal(true)
    }
  }

  return (
    <>
      {PAGE_SCHEMAS.map((schema, i) => (
        <script key={i} type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(schema) }} />
      ))}
      <Scroll segmentEventCommonProperties={segmentEventCommonProperties} />
      <AnalyticsViewPage
        isSemPage
        segmentEventCommonProperties={segmentEventCommonProperties}
        lob={Lob.BIKE}
        widget="bike-seo-widget"
        JourneyData={journeyData}
      />
      <div className={styles.page}>
        <HeroSection surface="brandSubtle">
          <div
            className={`${styles.sectionInner} mx-auto px-16 md:px-32 lg:px-[80px] xl:px-0 pt-20 pb-40 lg:pt-32 lg:pb-64 flex flex-col gap-16 lg:gap-40 min-w-0`}>
            <HeroContentClient
              continueInsuringJourney={continueInsuringJourney}
              continueInsuringSegmentEventProps={heroRegistrationFlow.continueInsuringSegmentEventProps}
              viewModel={heroViewModel}
              registrationFormProps={getRegistrationFormProps(heroRegistrationFlow, submitFromHero)}
              onNewBikeClick={heroRegistrationFlow.handleNewBikeClick}
              logoSrc={imgAckoLogo}
            />
          </div>
        </HeroSection>

        {/* Trust Bar: full-bleed; xl≈Figma 80px gutter, smaller padding on narrow viewports */}
        <TrustBar />

        {/* SEM landing page — single full-width column capped at 1280px */}
        <div className={`${styles.docWithSidebar} lg:px-0`}>
          <div className={styles.docMainColumn}>
            <Section>
              <CoveragesSectionClient />
            </Section>
            <Separator className="lg:hidden" />
            <Section>
              <WhyAckoSection />
            </Section>
            <Separator className="lg:hidden" />
            <Section>
              <ReviewsSection reviews={reviewsToShow} />
            </Section>
            <Separator className="lg:hidden" />
          </div>
        </div>

        {/* CTA: full-bleed purple */}
        <CTASection onCheckPriceClick={() => openInputModal(window.innerWidth < 768 ? 'bottom-sheet' : 'modal')} />

        <Footer data={footerData} />
        {/* Sticky Footer for Mobile */}
        <StickyFooterClient onCheckPriceClick={() => openInputModal('bottom-sheet')} />
        <BikeRegistrationInputModal
          show={showInputModal}
          onClose={() => setShowInputModal(false)}
          presentation={inputModalPresentation}
          registrationFormProps={getRegistrationFormProps(inputModalRegistrationFlow, submitFromInputModal)}
          onNewBikeClick={inputModalRegistrationFlow.handleNewBikeClick}
          viewModel={heroViewModel}
        />
        <SimpleBikeFlowModal
          show={isIllogicalFlowModalOpen}
          regNo={activeRegistrationFlow.regNumber}
          errorCode={activeRegistrationFlow.proposalAPIResponse?.error_code}
          illogicalFlowData={activeRegistrationFlow.proposalAPIResponse?.error_context}
          product={activeRegistrationFlow.product}
          setProposalAPIResponse={activeRegistrationFlow.setProposalAPIResponse}
          setShowIllogicalFlowModal={activeRegistrationFlow.setShowIllogicalFlowModal}
          onClose={activeRegistrationFlow.resetIllogicalFlow}
          onEditRegistration={handleEditRegistrationFromFlowModal}
        />
      </div>
    </>
  )
}

export default BikeInsurancePageClient
